// src/components/pages/admin-dash/UserTable.jsx
import React, { useMemo } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * All-users screen.
 *
 * Combines what used to be two views ("View User by ID" and "View All
 * Users") into one searchable, sortable, paginated table. Searching by
 * a numeric ID is just one possible search term.
 *
 * Props are unchanged-ish from the old UserTable so the parent's data
 * fetching logic doesn't have to move:
 *   users           full users array
 *   onEdit          (user) => void   opens EditUserModal
 *   onRowClick      (user) => void   opens UserModal (details)
 *   searchTerm, setSearchTerm
 *   filterType,  setFilterType
 *   filterStatus, setFilterStatus
 *   loading
 *   onRefresh       reload-button handler (optional)
 *   focusUserId     scroll-to / highlight a specific user (from overview)
 */
const TYPES = [
  { value: "",        label: "All types" },
  { value: "USER",    label: "User"      },
  { value: "ADMIN",   label: "Admin"     },
  { value: "SUPPORT", label: "Support"   },
];

const STATUSES = [
  { value: "",      label: "All status" },
  { value: "TRUE",  label: "Active"     },
  { value: "FALSE", label: "Inactive"   },
];

export default function UserTable({
  users = [],
  onEdit,
  onRowClick,
  onImpersonate,
  searchTerm, setSearchTerm,
  filterType, setFilterType,
  filterStatus, setFilterStatus,
  loading = false,
  onRefresh,
  focusUserId,
}) {
  const filtered = useMemo(() => {
    const q = (searchTerm || "").toLowerCase().trim();
    return users.filter((u) => {
      if (filterType && u.type !== filterType) return false;
      if (filterStatus && u.active !== filterStatus) return false;
      if (!q) return true;
      return (
        (u.name  || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        String(u.id || "").includes(q)
      );
    });
  }, [users, searchTerm, filterType, filterStatus]);

  const columns = [
    {
      key: "id",
      label: "ID",
      sort: true,
      width: "70px",
      render: (u) => (
        <span
          className="tabular-nums text-[12px]"
          style={{ color: focusUserId === u.id ? "var(--gold)" : "var(--txt-2)" }}
        >
          #{u.id}
        </span>
      ),
    },
    {
      key: "name",
      label: "Name",
      sort: true,
      render: (u) => (
        <div className="min-w-0">
          <div
            className="font-medium truncate"
            style={{ color: "var(--txt)" }}
          >
            {u.name || "—"}
          </div>
          <div className="text-[11px] truncate" style={{ color: "var(--txt-3)" }}>
            {u.email}
          </div>
        </div>
      ),
      value: (u) => u.name || "",
    },
    {
      key: "active",
      label: "Status",
      sort: true,
      width: "120px",
      render: (u) =>
        u.active === "TRUE" ? (
          <Badge variant="teal">Active</Badge>
        ) : (
          <Badge variant="red">Inactive</Badge>
        ),
    },
    {
      key: "type",
      label: "Role",
      sort: true,
      width: "100px",
      render: (u) => {
        if (u.type === "ADMIN") return <Badge variant="gold">Admin</Badge>;
        if (u.type === "SUPPORT") return <Badge variant="teal">Support</Badge>;
        return <Badge variant="neutral">User</Badge>;
      },
    },
    {
      key: "credit",
      label: "Credits",
      sort: true,
      align: "right",
      width: "100px",
      value: (u) => u.account?.credit ?? 0,
      render: (u) => (
        <span className="tabular-nums" style={{ color: "var(--txt)" }}>
          {u.account?.credit ?? 0}
        </span>
      ),
    },
    {
      key: "_actions",
      label: "",
      align: "right",
      width: "130px",
      render: (u) => (
        <div className="flex items-center justify-end gap-1">
          <IconBtn
            title="View details"
            icon="eye"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(u);
            }}
          />
          <IconBtn
            title="Edit user"
            icon="pencil"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(u);
            }}
          />
          {onImpersonate && (
            <IconBtn
              title="View as this user"
              icon="user-shield"
              onClick={(e) => {
                e.stopPropagation();
                onImpersonate(u);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  const hasFilters = !!(searchTerm || filterType || filterStatus);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className="font-semibold mb-1"
            style={{ fontSize: 22, color: "var(--txt)" }}
          >
            Users
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            {filtered.length} of {users.length} shown
          </p>
        </div>
        {onRefresh && (
          <Button icon="refresh" size="sm" variant="ghost" onClick={onRefresh} loading={loading}>Refresh
          </Button>
        )}
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_180px_180px_auto]">
          <Field
            placeholder="Search by name, email, or ID…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="!mb-0"
          />
          <SelectField
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            options={TYPES}
          />
          <SelectField
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={STATUSES}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setFilterType("");
                setFilterStatus("");
              }}
              className="text-[12px] hover:underline px-2"
              style={{ color: "var(--txt-2)" }}
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      <AdminTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyIcon="users"
        emptyMessage={
          users.length === 0
            ? "No users yet."
            : "No users match these filters."
        }
        onRowClick={onRowClick}
        defaultSort={{ key: "id", dir: "desc" }}
      />
    </div>
  );
}

/* ============================================================
   Tiny helpers
   ============================================================ */
function SelectField({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
        style={{
          background: "var(--surf-2)",
          color: "var(--txt)",
          borderColor: "var(--border-2)",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
        style={{ color: "var(--txt-3)" }}
      >
        ▾
      </span>
    </div>
  );
}

function IconBtn({ title, icon, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md transition-colors"
      style={{
        width: 30,
        height: 30,
        color: "var(--txt-2)",
        border: "1px solid transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--surf-2)";
        e.currentTarget.style.color      = "var(--gold)";
        e.currentTarget.style.borderColor = "var(--border-2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color      = "var(--txt-2)";
        e.currentTarget.style.borderColor = "transparent";
      }}
    >
      <Icon name={icon} size={14} />
    </button>
  );
}
